import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Zap, Loader2 } from 'lucide-react';

const SYMBOLS = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'XAUUSD', 'BTCUSD'];

const QuickTrade = ({ livePrices = {}, onTradeExecuted }) => {
  const [symbol, setSymbol] = useState('EURUSD');
  const [volume, setVolume] = useState('0.01');
  const [executing, setExecuting] = useState(null); // 'BUY' or 'SELL'

  const currentPrice = livePrices[symbol]?.bid || 0;

  const handleTrade = async (type) => {
    if (!volume || parseFloat(volume) <= 0) {
      toast.error('Please enter a valid volume');
      return;
    }

    setExecuting(type);
    try {
      const response = await api.executeTrade({
        symbol,
        type,
        volume: parseFloat(volume)
      });
      
      toast.success(
        `${type} ${volume} ${symbol} @ ${response.data.open_price.toFixed(5)}`,
        { description: `Ticket #${response.data.mt5_ticket}` }
      );
      
      onTradeExecuted?.(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trade execution failed');
    } finally {
      setExecuting(null);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '---';
    const decimals = symbol.includes('JPY') ? 3 : symbol.includes('XAU') || symbol.includes('BTC') ? 2 : 5;
    return price.toFixed(decimals);
  };

  return (
    <Card className="animate-fade-in-up" data-testid="quick-trade-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          Quick Trade
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Symbol Selection */}
        <div className="space-y-2">
          <Label>Symbol</Label>
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger data-testid="trade-symbol-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYMBOLS.map((s) => (
                <SelectItem key={s} value={s}>
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{s}</span>
                    {livePrices[s] && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatPrice(livePrices[s].bid)}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Current Price Display */}
        <div className="p-3 rounded-lg bg-muted/50 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Price</p>
          <p className="text-2xl font-bold font-mono tabular-nums">
            {formatPrice(currentPrice)}
          </p>
          {livePrices[symbol]?.change !== undefined && (
            <p className={`text-xs ${livePrices[symbol].change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {livePrices[symbol].change >= 0 ? '+' : ''}{livePrices[symbol].change}%
            </p>
          )}
        </div>

        {/* Volume Input */}
        <div className="space-y-2">
          <Label>Volume (Lots)</Label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            className="font-mono"
            data-testid="trade-volume-input"
          />
        </div>

        {/* Quick Volume Buttons */}
        <div className="flex gap-2">
          {['0.01', '0.1', '0.5', '1.0'].map((v) => (
            <Button
              key={v}
              variant="outline"
              size="sm"
              className="flex-1 font-mono text-xs"
              onClick={() => setVolume(v)}
            >
              {v}
            </Button>
          ))}
        </div>

        {/* Buy/Sell Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white glow-success"
            onClick={() => handleTrade('BUY')}
            disabled={executing !== null}
            data-testid="buy-btn"
          >
            {executing === 'BUY' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-2" />
            )}
            BUY
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white glow-error"
            onClick={() => handleTrade('SELL')}
            disabled={executing !== null}
            data-testid="sell-btn"
          >
            {executing === 'SELL' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-2" />
            )}
            SELL
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Market execution • Demo mode
        </p>
      </CardContent>
    </Card>
  );
};

export default QuickTrade;
